/* GB homepage v7 (2026-10-19): runtime for the gbh7 homepage sections. Loaded deferred by each section that needs it;
   a second copy of this file returns at once, so every listener exists once.
   - Players ([data-gbh7-video]), v2 for phones (Emi: "make the video players optimized for mobile"): poster first;
     no video URL is attached before an intentional tap. The whole picture is one play/pause button
     ([data-gbh7-play]); the first tap plays with sound, a blocked unmuted start retries once muted. Sound
     ([data-gbh7-sound]) and full-screen ([data-gbh7-fullscreen]) buttons and a thin progress bar
     ([data-gbh7-progress]) show once started. Native controls are never shown inline, only inside full screen.
     One player plays at a time on the whole page. A player stops when scrolled or swiped out of view, when the tab is
     hidden and on pagehide; at the end it rewinds and keeps its source for an instant replay. Every start gets a
     generation number: a play() promise from an older request can never restart a clip that was paused.
   - Rails ([data-gbh7-rail]): 44 px previous/next buttons that are disabled at the edges and hidden when nothing
     scrolls, a swipe cue, smooth scrolling unless the visitor prefers reduced motion.
   - Demo steps ([data-gbh7-steps] [data-gbh7-time]): seek the demo video to a verified time and play it.
   - Pictures ([data-gbh7-lazy], v3 speed): the HTML carries a 1x1 data GIF; the real data-gbh7-src/-srcset are
     swapped in once by one IntersectionObserver (300px / 160px ahead) or on play, so nothing loads on first paint.
   Theme editor: new or reloaded sections are set up once (shopify:section:load); removed ones are stopped. */
(function () {
  'use strict';

  var existing = window.GBH7Home;
  if (existing && existing.version) {
    if (typeof existing.scan === 'function') existing.scan(document);
    return;
  }

  var MOBILE = '(max-width: 749px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';

  function matches(query) {
    try { return !!(window.matchMedia && window.matchMedia(query).matches); } catch (error) { return false; }
  }
  function toArray(list) { return Array.prototype.slice.call(list || []); }
  function players(scope) { return toArray((scope || document).querySelectorAll('[data-gbh7-video]')); }
  function videoOf(player) { return player.querySelector('video'); }
  function state(player) {
    return player.__gbh7 || (player.__gbh7 = { desired: false, generation: 0, seek: null, full: false });
  }
  function labelOf(player) { return player.getAttribute('data-label') || 'video'; }
  function isActive(player) { return player.getAttribute('data-state') === 'active'; }
  function fullscreenElement() { return document.fullscreenElement || document.webkitFullscreenElement || null; }
  function isFull(player) {
    var video = videoOf(player);
    return !!video && (state(player).full || fullscreenElement() === video);
  }

  function visible(player) {
    if (document.hidden || player.isConnected === false) return false;
    var r = player.getBoundingClientRect();
    var clipEl = player.closest('[data-gbh7-track]') || player;
    var clip = clipEl.getBoundingClientRect();
    var width = window.innerWidth || document.documentElement.clientWidth;
    var height = window.innerHeight || document.documentElement.clientHeight;
    return r.right > Math.max(0, clip.left) && r.left < Math.min(width, clip.right) &&
      r.bottom > Math.max(0, clip.top) && r.top < Math.min(height, clip.bottom);
  }

  function sync(player) {
    var video = videoOf(player);
    if (!video) return;
    var playing = state(player).desired && !video.paused && !video.ended;
    var active = isActive(player);
    var label = labelOf(player);
    player.classList.toggle('is-playing', playing);
    player.classList.toggle('is-active', active);
    if (!active) player.classList.remove('is-shown');
    var button = player.querySelector('[data-gbh7-play]');
    if (button) {
      button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      button.setAttribute('aria-label', (playing ? 'Pause ' : 'Play ') + label);
    }
    var sound = player.querySelector('[data-gbh7-sound]');
    if (sound) {
      sound.hidden = !active;
      sound.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
      sound.setAttribute('aria-label', (video.muted ? 'Turn sound on ' : 'Turn sound off ') + label);
      player.classList.toggle('is-muted', !!video.muted);
    }
    var full = player.querySelector('[data-gbh7-fullscreen]');
    if (full) full.hidden = !active;
    var progress = player.querySelector('[data-gbh7-progress]');
    if (progress) progress.hidden = !active;
  }

  function stop(player) {
    var s = state(player);
    s.desired = false;
    s.generation += 1;
    var video = videoOf(player);
    // pause() also cancels a pending play() whose promise has not settled yet.
    if (video) video.pause();
    sync(player);
  }

  function stopAll(except) {
    players(document).forEach(function (player) { if (player !== except) stop(player); });
  }

  function reveal(img) {
    if (!img || !img.hasAttribute || !img.hasAttribute('data-gbh7-lazy')) return;
    var srcset = img.getAttribute('data-gbh7-srcset');
    var src = img.getAttribute('data-gbh7-src');
    img.removeAttribute('data-gbh7-lazy');
    if (lazyObserver) lazyObserver.unobserve(img);
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
  }

  var lazyObserver = null;
  if ('IntersectionObserver' in window) {
    lazyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { if (entry.isIntersecting) reveal(entry.target); });
    }, { rootMargin: '300px 160px' });
  }

  function bindLazy(img) {
    if (img.__gbh7Lazy) return;
    img.__gbh7Lazy = true;
    if (lazyObserver) lazyObserver.observe(img); else reveal(img);
  }

  function attach(player, video) {
    var posterImg = player.querySelector('[data-gbh7-poster]');
    reveal(posterImg);
    if (video.getAttribute('src')) return true;
    var src = matches(MOBILE)
      ? (player.getAttribute('data-src-mobile') || player.getAttribute('data-src'))
      : (player.getAttribute('data-src') || player.getAttribute('data-src-mobile'));
    if (!src) return false;
    var posterUrl = '';
    if (posterImg) {
      posterUrl = posterImg.currentSrc || posterImg.getAttribute('src') || '';
      if (posterUrl.indexOf('data:') === 0) posterUrl = posterImg.getAttribute('src') || '';
    }
    if (posterUrl && posterUrl.indexOf('data:') !== 0) video.setAttribute('poster', posterUrl);
    video.setAttribute('src', src);
    return true;
  }

  // sound: true only from a visitor tap. A fresh start (idle or after the end) then plays with sound; resuming a
  // paused clip keeps the visitor's own sound choice.
  function start(player, sound) {
    var video = videoOf(player);
    if (!video) return;
    if (!visible(player)) { stop(player); return; }
    var fresh = !isActive(player);
    if (!attach(player, video)) { stop(player); return; }
    stopAll(player);
    if (sound && fresh) video.muted = false;
    player.setAttribute('data-state', 'active');
    player.classList.remove('is-error');
    var s = state(player);
    s.desired = true;
    var generation = ++s.generation;
    function current() { return s.desired && s.generation === generation; }
    function settled() {
      if (!current()) {
        // Never pause a newer request on this player when an older promise settles.
        if (!s.desired) stop(player);
        return;
      }
      if (!visible(player) && !isFull(player)) stop(player); else sync(player);
    }
    function attempt(retried) {
      function failed(error) {
        if (!current()) return;
        if (!visible(player)) { stop(player); return; }
        var name = error && error.name;
        if (!retried && (name === 'NotAllowedError' || name === 'AbortError')) {
          // The browser refused sound without a fresh gesture: the one retry is muted (sound button turns it on).
          if (name === 'NotAllowedError') video.muted = true;
          sync(player);
          attempt(true);
          return;
        }
        // Decode, network, source and repeated policy failures never restart playback.
        stop(player);
        player.classList.add('is-error');
      }
      try {
        var promise = video.play();
        sync(player);
        if (promise && typeof promise.then === 'function') promise.then(settled, failed); else settled();
      } catch (error) { failed(error); }
    }
    attempt(false);
  }

  function toggle(player) {
    var video = videoOf(player);
    if (state(player).desired && video && !video.ended) stop(player); else start(player, true);
  }

  function reset(player) {
    // End of the clip (or section removed): back to the poster and play icon, source kept for an instant replay.
    stop(player);
    var video = videoOf(player);
    if (video) {
      try { video.currentTime = 0; } catch (error) {}
    }
    player.setAttribute('data-state', 'idle');
    setProgress(player, 0);
    sync(player);
  }

  function setProgress(player, ratio) {
    var progress = player.querySelector('[data-gbh7-progress]');
    var bar = progress ? progress.querySelector('span') : null;
    if (bar) bar.style.transform = 'scaleX(' + Math.max(0, Math.min(1, ratio || 0)) + ')';
  }

  function enterFullscreen(player) {
    var video = videoOf(player);
    if (!video) return;
    try {
      if (typeof video.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen();
      } else if (typeof video.requestFullscreen === 'function') {
        var promise = video.requestFullscreen();
        if (promise && typeof promise.catch === 'function') promise.catch(function () {});
      }
    } catch (error) {}
  }

  function setFull(player, on) {
    var video = videoOf(player);
    if (!video) return;
    state(player).full = !!on;
    // Native controls only while the video is full screen; never inline.
    video.controls = !!on;
    sync(player);
  }

  function onFullscreenChange() {
    var element = fullscreenElement();
    players(document).forEach(function (player) {
      var video = videoOf(player);
      if (!video) return;
      var on = element === video;
      if (on || state(player).full || video.controls) setFull(player, on);
    });
  }

  function onPlay(player, video) {
    if (video.paused) { sync(player); return; }
    var s = state(player);
    if (!visible(player) && !isFull(player)) { stop(player); return; }
    if (!s.desired) {
      // Playback started outside our button (full-screen controls, media keys): it becomes the only player.
      s.desired = true;
      s.generation += 1;
      player.setAttribute('data-state', 'active');
      stopAll(player);
    }
    sync(player);
  }

  function seek(player, seconds) {
    var video = videoOf(player);
    if (!video || !isFinite(seconds) || seconds < 0) return;
    var s = state(player);
    s.seek = seconds;
    function apply() {
      if (s.seek !== seconds) return;
      try { video.currentTime = seconds; s.seek = null; } catch (error) {}
    }
    var running = s.desired && !video.paused && !video.ended;
    if (!running) start(player, true);
    if (!video.getAttribute('src')) { s.seek = null; return; }
    if (video.readyState >= 1) apply();
    else {
      video.addEventListener('loadedmetadata', function once() {
        video.removeEventListener('loadedmetadata', once);
        apply();
      });
    }
  }

  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting && state(entry.target).desired && !isFull(entry.target)) stop(entry.target);
      });
    }, { threshold: 0.25 });
  }

  function bindPlayer(player) {
    if (player.__gbh7Bound) return;
    var video = videoOf(player);
    if (!video) return;
    player.__gbh7Bound = true;
    if (!player.getAttribute('data-state')) player.setAttribute('data-state', 'idle');
    video.controls = false;
    var button = player.querySelector('[data-gbh7-play]');
    if (button) button.addEventListener('click', function (event) { event.preventDefault(); toggle(player); });
    var sound = player.querySelector('[data-gbh7-sound]');
    if (sound) {
      sound.addEventListener('click', function (event) {
        event.preventDefault();
        video.muted = !video.muted;
        sync(player);
      });
    }
    var full = player.querySelector('[data-gbh7-fullscreen]');
    if (full) full.addEventListener('click', function (event) { event.preventDefault(); enterFullscreen(player); });
    var progressQueued = false;
    video.addEventListener('timeupdate', function () {
      if (progressQueued) return;
      progressQueued = true;
      var paint = function () {
        progressQueued = false;
        var duration = video.duration;
        setProgress(player, duration > 0 && isFinite(duration) ? (video.currentTime || 0) / duration : 0);
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(paint); else setTimeout(paint, 16);
    });
    video.addEventListener('play', function () { onPlay(player, video); });
    video.addEventListener('playing', function () {
      if (isActive(player)) player.classList.add('is-shown');
      onPlay(player, video);
    });
    video.addEventListener('loadeddata', function () { if (isActive(player)) player.classList.add('is-shown'); });
    video.addEventListener('pause', function () {
      if (video.paused && state(player).desired) stop(player); else sync(player);
    });
    video.addEventListener('ended', function () { reset(player); });
    video.addEventListener('error', function () {
      if (!video.getAttribute('src')) return;
      stop(player);
      player.classList.add('is-error');
    });
    video.addEventListener('volumechange', function () { sync(player); });
    video.addEventListener('webkitbeginfullscreen', function () { setFull(player, true); });
    video.addEventListener('webkitendfullscreen', function () { setFull(player, false); });
    if (observer) observer.observe(player);
    sync(player);
  }

  function railParts(rail) {
    return {
      track: rail.querySelector('[data-gbh7-track]'),
      prev: rail.querySelector('[data-gbh7-prev]'),
      next: rail.querySelector('[data-gbh7-next]'),
      nav: rail.querySelector('[data-gbh7-nav]')
    };
  }

  function maxScroll(track) { return Math.max(0, track.scrollWidth - track.clientWidth); }

  function updateRail(rail) {
    var parts = railParts(rail);
    if (!parts.track) return;
    var max = maxScroll(parts.track);
    var left = parts.track.scrollLeft;
    var scrollable = max > 2;
    rail.classList.toggle('is-scrollable', scrollable);
    if (parts.nav && parts.nav.hidden !== !scrollable) parts.nav.hidden = !scrollable;
    // Native scroll snapping can retain the row's 2px start padding, so the edges allow 4px.
    var atStart = !scrollable || left <= 4;
    var atEnd = !scrollable || left >= max - 4;
    [[parts.prev, atStart, parts.next, !atEnd], [parts.next, atEnd, parts.prev, !atStart]].forEach(function (row) {
      var button = row[0];
      if (!button || button.disabled === row[1]) return;
      // Keep keyboard focus on the rail when the focused arrow reaches its edge.
      if (row[1] && document.activeElement === button && row[2] && row[3]) {
        try { row[2].focus({ preventScroll: true }); } catch (error) { row[2].focus(); }
      }
      button.disabled = row[1];
    });
  }

  function pageStep(track) {
    var items = toArray(track.children).filter(function (el) { return !el.hidden; });
    if (!items.length) return track.clientWidth;
    var first = items[0].getBoundingClientRect();
    var size = first.width || track.clientWidth;
    var gap = 0;
    if (items[1]) gap = Math.max(0, items[1].getBoundingClientRect().left - first.right);
    var perPage = Math.max(1, Math.floor((track.clientWidth + gap) / (size + gap)));
    return perPage * (size + gap);
  }

  function scrollRail(rail, direction) {
    var track = railParts(rail).track;
    if (!track) return;
    var target = Math.max(0, Math.min(maxScroll(track), track.scrollLeft + direction * pageStep(track)));
    var behavior = matches(REDUCED) ? 'auto' : 'smooth';
    if (typeof track.scrollTo === 'function') track.scrollTo({ left: target, behavior: behavior });
    else track.scrollLeft = target;
  }

  function bindRail(rail) {
    if (rail.__gbh7Rail) return;
    var parts = railParts(rail);
    if (!parts.track) return;
    rail.__gbh7Rail = true;
    if (parts.prev) parts.prev.addEventListener('click', function () { scrollRail(rail, -1); });
    if (parts.next) parts.next.addEventListener('click', function () { scrollRail(rail, 1); });
    parts.track.addEventListener('scroll', function () { queue(rail); }, { passive: true });
    updateRail(rail);
  }

  function setCurrentStep(list, time) {
    var buttons = toArray(list.querySelectorAll('[data-gbh7-time]'));
    var currentIndex = -1;
    buttons.forEach(function (button, index) {
      var at = parseFloat(button.getAttribute('data-gbh7-time'));
      if (isFinite(at) && time + 0.05 >= at) currentIndex = index;
    });
    buttons.forEach(function (button, index) {
      if (index === currentIndex) button.setAttribute('aria-current', 'step');
      else button.removeAttribute('aria-current');
    });
  }

  function bindSteps(list) {
    if (list.__gbh7Steps) return;
    var root = list.closest('[data-gbh7-demo]') || list.parentNode;
    var player = root ? root.querySelector('[data-gbh7-video]') : null;
    if (!player) return;
    var video = videoOf(player);
    if (!video) return;
    list.__gbh7Steps = true;
    toArray(list.querySelectorAll('[data-gbh7-time]')).forEach(function (button) {
      button.addEventListener('click', function () {
        var seconds = parseFloat(button.getAttribute('data-gbh7-time'));
        if (!isFinite(seconds)) return;
        setCurrentStep(list, seconds);
        seek(player, seconds);
      });
    });
    video.addEventListener('timeupdate', function () {
      if (isActive(player)) setCurrentStep(list, video.currentTime || 0);
    });
  }

  function scan(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    players(root).forEach(bindPlayer);
    toArray(root.querySelectorAll('[data-gbh7-lazy]')).forEach(bindLazy);
    toArray(root.querySelectorAll('[data-gbh7-rail]')).forEach(bindRail);
    toArray(root.querySelectorAll('[data-gbh7-steps]')).forEach(bindSteps);
  }

  function stopOffscreen() {
    players(document).forEach(function (player) {
      if (state(player).desired && !visible(player) && !isFull(player)) stop(player);
    });
  }

  // One animation frame per burst of scroll/resize events. Page scrolling only re-checks playing players;
  // a rail's arrows are re-read when that rail scrolls, and every rail after resize, load or a section reload.
  var queued = false;
  var dirtyRails = [];
  var allRailsDirty = false;
  function queue(rail) {
    if (rail === true) allRailsDirty = true;
    else if (rail && rail.querySelector && dirtyRails.indexOf(rail) < 0) dirtyRails.push(rail);
    if (queued) return;
    queued = true;
    var run = function () {
      queued = false;
      var rails = allRailsDirty ? toArray(document.querySelectorAll('[data-gbh7-rail]')) : dirtyRails;
      dirtyRails = [];
      allRailsDirty = false;
      stopOffscreen();
      rails.forEach(updateRail);
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run); else setTimeout(run, 16);
  }

  document.addEventListener('visibilitychange', function () { if (document.hidden) stopAll(); });
  document.addEventListener('fullscreenchange', onFullscreenChange);
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);
  window.addEventListener('pagehide', function () { stopAll(); });
  window.addEventListener('scroll', function () { queue(); }, { capture: true, passive: true });
  window.addEventListener('resize', function () { queue(true); }, { passive: true });
  window.addEventListener('load', function () { queue(true); });
  document.addEventListener('shopify:section:load', function (event) { scan(event.target); queue(true); });
  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target && event.target.querySelectorAll) players(event.target).forEach(reset);
  });

  window.GBH7Home = { version: '1.0.0', scan: scan, stopAll: stopAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(document); }, { once: true });
  else scan(document);
})();
